import { useState, useRef, useCallback, useEffect } from "react";
import { Box, Button, Chip, CircularProgress, List, ListItem, ListItemText, Paper, Tooltip, Typography } from "@material-ui/core";
import { TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { blue } from "@material-ui/core/colors";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloseIcon from "@material-ui/icons/Close";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
    cloneWrapper: {
        position: "relative",
        display: "inline-block",
    },
    cloneButton: {
        backgroundColor: blue[600],
        color: "#fff",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8rem",
        padding: "5px 14px",
        "&:hover": {
            backgroundColor: blue[800],
        },
    },
    searchDropdownWrapper: {
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        zIndex: 1300,
        width: 360,
        boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "#fff",
    },
    searchInputBox: {
        padding: "10px 12px 6px",
        borderBottom: "1px solid #eee",
    },
    suggestionList: {
        maxHeight: 260,
        overflowY: "auto",
        padding: 0,
    },
    suggestionItem: {
        cursor: "pointer",
        padding: "8px 14px",
        borderBottom: "1px solid #f5f5f5",
        "&:hover": {
            backgroundColor: blue[50],
        },
    },
    noResult: {
        padding: "12px 14px",
        color: "#888",
        fontSize: "0.85rem",
    },
    clonedChip: {
        marginLeft: 8,
        backgroundColor: blue[100],
        color: blue[900],
        fontWeight: 600,
        fontSize: "0.75rem",
    },
}));

const CloneUserButton = ({ token, listitems, selectedItems, setSelectedItems }) => {
    const classes = useStyles();

    const [showCloneSearch, setShowCloneSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [allUsers, setAllUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [clonedFromUser, setClonedFromUser] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    const dropdownRef = useRef(null);
    const hasFetched = useRef(false);

    const fetchUsers = useCallback(async () => {
        if (hasFetched.current) return;
        setLoadingUsers(true);
        setFetchError(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_APIHOST}/api/getallusers`, {
                headers: { authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`Error ${res.status}`);
            const data = await res.json();
            const users = Array.isArray(data) ? data : data.users || [];
            setAllUsers(users);
            hasFetched.current = true;
        } catch (err) {
            setFetchError("Failed to load users. Please try again.");
        } finally {
            setLoadingUsers(false);
        }
    }, [token]);

    const handleOpen = () => {
        setShowCloneSearch(true);
        fetchUsers();
    };

    const handleClose = () => {
        setShowCloneSearch(false);
        setSearchQuery("");
        setFilteredUsers([]);
    };

    // Debounced search
    const debouncedSearch = useCallback(
        _.debounce((query) => {
            if (!query.trim()) {
                setFilteredUsers([]);
                return;
            }
            const q = query.toLowerCase();
            const results = allUsers.filter((u) => {
                const name = (u.profile?.displayName || "").toLowerCase();
                const email = (u.profile?.email || "").toLowerCase();
                const first = (u.profile?.firstName || "").toLowerCase();
                const last = (u.profile?.lastName || "").toLowerCase();
                return name.includes(q) || email.includes(q) || first.includes(q) || last.includes(q);
            });
            setFilteredUsers(results.slice(0, 10));
        }, 250),
        [allUsers]
    );

    useEffect(() => {
        debouncedSearch(searchQuery);
        return () => debouncedSearch.cancel();
    }, [searchQuery, debouncedSearch]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                handleClose();
            }
        };
        if (showCloneSearch) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showCloneSearch]);

   const handleSelectUser = (user) => {
    const storeGUIDs = user.profile?.storeGUIDs || [];
    if (!storeGUIDs.length) {
        alert(`${user.profile?.displayName} has no stores assigned.`);
        return;
    }
    const matchedStores = listitems
    ? listitems.filter((store) =>
        storeGUIDs.some(guid => guid.toUpperCase() === store.FranchiseID.toUpperCase())
      )
    : [];

    const matchedGUIDs = matchedStores.map((store) => store.FranchiseID);
setSelectedItems(matchedGUIDs);
    setClonedFromUser(user.profile?.displayName || user.profile?.email);
    handleClose();
};

    return (
        <Box display="flex" alignItems="center">
            <div className={classes.cloneWrapper} ref={dropdownRef}>
                <Button
                    className={classes.cloneButton}
                    variant="contained"
                    size="small"
                    startIcon={<PeopleAltIcon style={{ fontSize: 16 }} />}
                    onClick={showCloneSearch ? handleClose : handleOpen}>
                    Clone User Stores
                </Button>

                {showCloneSearch && (
                    <Paper className={classes.searchDropdownWrapper} elevation={6}>
                        <Box className={classes.searchInputBox}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Search by name or email…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    endAdornment: loadingUsers ? <CircularProgress size={16} /> : null,
                                }}
                            />
                        </Box>

                        {fetchError && (
                            <Typography className={classes.noResult} style={{ color: "red" }}>
                                {fetchError}
                            </Typography>
                        )}

                        {!loadingUsers && !fetchError && searchQuery && filteredUsers.length === 0 && (
                            <Typography className={classes.noResult}>
                                No users found for "{searchQuery}"
                            </Typography>
                        )}

                        {!searchQuery && !loadingUsers && !fetchError && (
                            <Typography className={classes.noResult}>
                                Start typing a name or email…
                            </Typography>
                        )}

                        {filteredUsers.length > 0 && (
                            <List className={classes.suggestionList} disablePadding>
                                {filteredUsers.map((user, i) => (
                                    <ListItem
                                        key={user.id || i}
                                        className={classes.suggestionItem}
                                        onClick={() => handleSelectUser(user)}
                                        disableGutters>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Typography style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                                                        {user.profile?.displayName || `${user.profile?.firstName} ${user.profile?.lastName}`}
                                                    </Typography>
                                                    <Tooltip title="Clone this user's stores">
                                                        <FileCopyIcon style={{ fontSize: 15, color: blue[400], marginLeft: 6 }} />
                                                    </Tooltip>
                                                </Box>
                                            }
                                            secondary={
                                                <Typography style={{ fontSize: "0.78rem", color: "#666" }}>
                                                    {user.profile?.email}
                                                    {user.profile?.storeGUIDs?.length
                                                        ? ` · ${user.profile.storeGUIDs.length} store(s)`
                                                        : " · no stores"}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                )}
            </div>

            {clonedFromUser && (
                <Chip
                    className={classes.clonedChip}
                    icon={<FileCopyIcon style={{ fontSize: 14 }} />}
                    label={`Cloned from ${clonedFromUser}`}
                    onDelete={() => {
                        setSelectedItems([]);
                        setClonedFromUser(null);
                    }}
                    deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
                    size="small"
                />
            )}
        </Box>
    );
};

export default CloneUserButton;